import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Pagination,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { getSalesReturnReportData, getStockSalesReturnReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./StockReturn.css";

const tableHdrs = [
  "returnDate",
  "invoiceNumber",
  "productName",
  "taxRefund",
  "totalRefund",
  "customerUserName",
  "petName",
];

const StockSalesReturnReport = ({ returnType = "Sales", setReturnType }) => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const { stockSalesReturn, salesReturn } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getStockSalesReturnReportData());
    dispatch(getSalesReturnReportData("?filter=credit&type=credit"));
  }, [dispatch]);

  const overviewData = stockSalesReturn?.overview || {};
  const cards = overviewData?.cards || {};

  const summaryCards = [
    {
      title: "Line items returned",
      value:
        cards?.lineItemsReturnedDisplay ??
        (cards?.lineItemsReturned !== undefined
          ? `${cards.lineItemsReturned}`
          : "17"),
    },
    {
      title: "Total refund",
      value:
        cards?.totalRefundDisplay ??
        (cards?.totalRefund !== undefined
          ? `₹${Number(cards.totalRefund).toLocaleString()}`
          : "₹12,139"),
    },
  ];

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const getReqMainList = () => {
    return (salesReturn?.data ?? [])?.filter(
      (li) => li?.creditSave?.toLowerCase() === "credit",
    );
  };

  const getRequiredTableList = () => {
    if (Array.isArray(overviewData?.returns) && overviewData.returns.length > 0) {
      return overviewData.returns.flatMap((ret) => {
        const details = Array.isArray(ret?.details) && ret.details.length > 0 ? ret.details : [{}];
        return details.map((dt) => ({
          createdDate: ret?.returnDate,
          updatedDate: ret?.returnDate,
          invoiceNumbers: ret?.referenceNo || ret?.invoiceNo,
          userName: ret?.userName,
          petName: ret?.petName,
          name: dt?.name || dt?.tradeName || "Item",
          tax: Math.abs(dt?.tax ?? ret?.tax ?? 0),
          total: Math.abs(dt?.total ?? ret?.refundAmount ?? ret?.totalAmt ?? 0),
          ...dt,
        }));
      });
    }

    const reqList = (getReqMainList() ?? [])?.flatMap((li) =>
      li?.paymentDetails?.map((inv) => ({
        createdDate: li?.createdDate,
        updatedDate: li?.updatedDate,
        invoiceNumbers: li?.invoiceNumbers,
        petShopInvoiceNumber: li?.petShopInvoiceNumber,
        treatmentInvoiceNumber: li?.treatmentInvoiceNumber,
        userName: li?.userName,
        petName: li?.petName,
        ...inv,
      })),
    );
    return reqList;
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((getRequiredTableList()?.length ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    setPage(selectedPage);
  };

  const getTableData = () => {
    return (getRequiredTableList() ?? [])
      ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
      ?.map((li) => {
        const invoices = [
          li?.referenceNo ?? "",
          li?.invoiceNo ?? "",
          li?.petShopInvoiceNumber ?? "",
          li?.treatmentInvoiceNumber ?? "",
          li?.invoiceNumbers ?? "",
        ]?.filter(Boolean);
        return {
          ...li,
          returnDate: moment(
            new Date(li?.returnDate ?? li?.updatedDate ?? li?.createdDate),
          ).format("DD MMM YYYY"),
          invoiceNumber: invoices?.length > 0 ? invoices?.join(", ") : "-",
          customerUserName: li?.userName ?? "",
          petName: li?.petName ?? "",
          taxRefund: Number(Math.abs(li?.tax ?? 0))?.toFixed(2),
          totalRefund: Number(Math.abs(li?.total ?? li?.totalAmt ?? li?.refundAmount ?? 0))?.toFixed(2),
          productName: li?.name ?? li?.tradeName ?? "",
        };
      });
  };

  const handleExportExcel = () => {
    const tableData = getRequiredTableList() ?? [];
    const exportData = tableData.map((row) => {
      const invoices = [
        row?.petShopInvoiceNumber ?? "",
        row?.treatmentInvoiceNumber ?? "",
        row?.invoiceNumbers ?? "",
      ]?.filter(Boolean);
      return {
        "Return Date": moment(new Date(row?.updatedDate ?? row?.createdDate)).format("DD MMM YYYY"),
        "Invoice No.": invoices?.length > 0 ? invoices?.join(", ") : "-",
        "Product Name": row?.name ?? "",
        "Tax Refund": Number(row?.tax ?? 0)?.toFixed(2),
        "Total Refund": Number(row?.total ?? 0)?.toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Sales Returns");
    XLSX.writeFile(workbook, `Stock_Sales_Returns_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  return (
    <div className="stock-return-container">
      {/* Header Row with Toggle */}
      <div className="stock-return-header-row">
        <div>
          <h2 className="stock-return-title">Stock returns</h2>
          <div className="stock-return-subtitle">
            See Billing → Returns Report for the invoice-level view.
          </div>
        </div>

        <div className="stock-return-toggle-group">
          <button
            type="button"
            className={`stock-return-toggle-btn ${returnType === "Sales" ? "active" : ""}`}
            onClick={() => setReturnType && setReturnType("Sales")}
          >
            Sales
          </button>
          <button
            type="button"
            className={`stock-return-toggle-btn ${returnType === "Purchase" ? "active" : ""}`}
            onClick={() => setReturnType && setReturnType("Purchase")}
          >
            Purchase
          </button>
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="stock-return-export-group">
        <button type="button" className="stock-return-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="stock-return-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (2 Cards) */}
      <div className="stock-return-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="stock-return-card" key={idx}>
            <h4 className="stock-return-card-title">{card.title}</h4>
            <div className="stock-return-card-value-container">
              <h2 className="stock-return-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Drill-down Section */}
      <div className="flex-row-between-align-center mv10">
        <div className="stock-return-drilldown-header">Drill-down — returned items</div>
        <div
          className="flex-row-ali-cen cursor"
          onClick={handleEditColumn}
        >
          <EditOutlinedIcon sx={w20h20BlueIcon} />
          <Typography className="blue-color fs16 font-medium ml5">
            Edit columns ({crntTblHeders?.length})
          </Typography>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Showing {getTableData()?.length ?? 0} of {getRequiredTableList()?.length ?? 0} rows for the selected filters
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((getRequiredTableList()?.length ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default StockSalesReturnReport;
